import type { Block, PaperPage } from "@/lib/papers-data";

function BlockView({ block, light }: { block: Block; light: boolean }) {
  const body = light ? "text-lightink/80" : "text-stone/80";
  const heading = light ? "text-lightink" : "text-ivory";
  const accent = light ? "text-lightaccent" : "text-gold";
  const line = light ? "border-lightline" : "border-white/10";

  switch (block.kind) {
    case "kicker":
      return <p className={`mb-4 font-mono text-[0.68rem] font-medium tracking-[0.2em] uppercase ${accent}`}>{block.text}</p>;
    case "heading":
      return <h2 className={`mt-8 mb-3 font-serif text-4xl font-light leading-none md:text-5xl ${heading}`}>{block.text}</h2>;
    case "subhead":
      return <h3 className={`mt-8 mb-2 font-serif text-2xl font-light ${heading}`}>{block.text}</h3>;
    case "standfirst":
      return <p className={`mb-7 font-serif text-xl font-light italic ${body}`}>{block.text}</p>;
    case "para":
      return <p className={`mb-5 text-[0.95rem] font-light leading-8 ${body}`}>{block.text}</p>;
    case "pull":
      return <p className={`my-9 border-y py-6 text-center font-serif text-3xl font-light leading-tight ${line} ${heading}`}>{block.text}</p>;
    case "quote":
      return (
        <figure className={`my-9 border-l-2 pl-5 ${line}`}>
          <blockquote className={`font-serif text-2xl font-light leading-snug ${heading}`}>“{block.text}”</blockquote>
          <figcaption className={`mt-3 font-mono text-[0.68rem] tracking-[0.16em] uppercase ${accent}`}>{block.attribution}</figcaption>
          {block.note ? <p className={`mt-2 text-xs leading-5 ${body}`}>{block.note}</p> : null}
        </figure>
      );
    case "signoff":
      return <p className={`my-6 font-serif text-xl font-light ${heading}`}>{block.text}</p>;
    case "rule":
      return <hr className={`my-10 border-0 border-t ${line}`} />;
    case "image":
      return (
        <figure className="my-8">
          <img
            src={block.src}
            alt={block.alt}
            width={block.width}
            height={block.height}
            loading="lazy"
            className={`photo-wash w-full ${block.frame ? `border p-2 ${line}` : ""}`}
          />
        </figure>
      );
    case "list":
      return (
        <ul className={`my-5 space-y-4 ${body}`}>
          {block.items.map((item) => (
            <li key={item} className={`border-l pl-4 text-[0.95rem] leading-7 ${line}`}>{item}</li>
          ))}
        </ul>
      );
    case "steps":
      return (
        <ol className="my-5 space-y-6">
          {block.items.map((item) => (
            <li key={item.label}>
              <p className={`font-mono text-[0.68rem] tracking-[0.18em] uppercase ${accent}`}>{item.label}</p>
              <p className={`mt-1 leading-7 ${body}`}>{item.text}</p>
            </li>
          ))}
        </ol>
      );
    case "words":
      return (
        <div className="my-12 space-y-5 text-center">
          {block.items.map((word) => (
            <p key={word} className={`font-serif text-3xl font-light ${heading}`}>{word}</p>
          ))}
        </div>
      );
    case "colophon":
      return (
        <div className={`mt-12 space-y-1 font-mono text-[0.68rem] tracking-[0.14em] uppercase ${body}`}>
          {block.lines.map((lineText) => (
            <p key={lineText}>{lineText}</p>
          ))}
        </div>
      );
  }
}

export function PaperPageView({ page, total }: { page: PaperPage; total: number }) {
  const light = page.tone === "ivory";
  return (
    <article
      data-testid={`paper-page-${page.n}`}
      className={`mt-7 border border-white/10 px-5 py-8 md:px-10 md:py-12 ${
        light ? "bg-softivory" : page.tone === "cover" ? "bg-navy" : "bg-charcoal"
      }`}
    >
      {page.hero ? (
        <figure className="-mx-5 -mt-8 mb-9 md:-mx-10 md:-mt-12">
          <img
            src={page.hero.src}
            alt={page.hero.alt}
            width={page.hero.width}
            height={page.hero.height}
            loading={page.n === 1 ? "eager" : "lazy"}
            className="photo-wash max-h-[72vh] w-full object-cover"
          />
        </figure>
      ) : null}
      {page.blocks.map((block, index) => (
        <BlockView key={`${block.kind}-${index}`} block={block} light={light} />
      ))}
      <p className={`mt-12 font-mono text-[0.65rem] tracking-[0.2em] uppercase ${light ? "text-lightink/60" : "text-stone/50"}`}>
        Page {page.n} of {total}
      </p>
    </article>
  );
}
